import { ChangeEvent, useCallback, useState } from 'react';

import { Button, Input, Typography } from '@braskai/ui-kit';

import { Stack } from '@mui/material';
import { z } from 'zod';

import { useInterfaceLanguage } from '@/shared/i18n';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { Member } from '../lib/types';

const emailSchema = z.string().email();

type MemberInviteProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  members: Member[];
  totalAllowedSeats: number;
  onInvite: (email: string) => void;
};

export const MemberInvite = ({
  isDisabled = false,
  isLoading = false,
  members,
  totalAllowedSeats,
  onInvite,
}: MemberInviteProps) => {
  const { t } = useInterfaceLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<null | string>(null);

  const validateEmail = useCallback(
    (value: string) => {
      if (!value) {
        setError(null);
        return false;
      }

      try {
        emailSchema.parse(value);

        const isDuplicateEmail = members.some((member) => member.email === value);

        if (isDuplicateEmail) {
          setError(t('teamspace.member.duplicateEmail'));
          return false;
        }

        setError(null);
        return true;
      } catch (err) {
        if (err instanceof z.ZodError) {
          setError(t('teamspace.member.incorrectEmail'));
        }

        return false;
      }
    },
    [members, t],
  );

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (!value) {
      validateEmail(value);
    }

    setEmail(value);
  };

  const handleInvite = () => {
    if (validateEmail(email)) {
      onInvite(email);
      setEmail('');
      setError(null);
      MixpanelEvents.inviteMemberSpace();
    }
  };

  return (
    <Stack gap={8}>
      <Typography color="grey.700" variant="body-2-md">
        {t('teamspace.member.count', { count: members.length, limit: totalAllowedSeats })}
      </Typography>
      <Stack direction="row" gap={8}>
        <Input
          data-testid="teamspace-invite-member-input"
          disabled={isDisabled}
          placeholder={t('teamspace.member.emailPlaceholder')}
          value={email}
          onChange={handleEmailChange}
        />
        <Button
          sx={{
            flexShrink: 0,
            minWidth: '74px',
          }}
          data-testid="teamspace-invite-member-button"
          disabled={!email || isDisabled || isLoading}
          isLoading={isLoading}
          type="button"
          variant="outlined"
          onClick={handleInvite}
        >
          {t('teamspace.member.invite')}
        </Button>
      </Stack>
      {error && (
        <Typography color="error.500" variant="body-2">
          {error}
        </Typography>
      )}
    </Stack>
  );
};
